// src/config/permissions.js (New File)
// OR define directly in roleService.js if preferred

export const PERMISSION_MODULES = {
    DASHBOARD: 'dashboard',
    LOANS: 'loans', // Use 'accounts' if you renamed consistently
    BORROWERS: 'borrowers', // Use 'debtors' if renamed
    PAYMENTS: 'payments',
    FEES: 'fees',
    INVESTORS: 'investors',
    REMITTANCES: 'remittances',
    REPORTS: 'reports', // Use 'reporting' if renamed
    ACCOUNTING: 'accounting', // Group for COA, JE, Recon, Audit
    USERS: 'users',
    ROLES: 'roles',
    LOOKUPS: 'lookups',
    SETTINGS: 'settings',
    // Add other top-level modules like Portfolios, Tasks etc. if implemented
};

// Define specific actions within modules
// Use consistent naming: view, create, edit, delete, process (for actions like payoff/remit)
export const ALL_PERMISSIONS = {
    [PERMISSION_MODULES.DASHBOARD]: {
        view: { key: 'dashboard_view', label: 'View Dashboard' },
    },
    [PERMISSION_MODULES.LOANS]: {
        view: { key: 'loans_view', label: 'View Loans/Accounts' },
        create: { key: 'loans_create', label: 'Create Loans/Accounts' },
        edit: { key: 'loans_edit', label: 'Edit Loans/Accounts' },
        delete: { key: 'loans_delete', label: 'Delete Loans/Accounts' },
        payoff: { key: 'loans_payoff', label: 'Process Loan Payoff' },
        manageNonAccrual: { key: 'loans_nonaccrual', label: 'Manage Non-Accrual Status' },
        manageEscrow: { key: 'loans_escrow', label: 'Manage Escrow Settings/Disburse' }, // Combined for simplicity
    },
    [PERMISSION_MODULES.BORROWERS]: {
        view: { key: 'borrowers_view', label: 'View Borrowers/Debtors' },
        create: { key: 'borrowers_create', label: 'Create Borrowers/Debtors' },
        edit: { key: 'borrowers_edit', label: 'Edit Borrowers/Debtors' },
        delete: { key: 'borrowers_delete', label: 'Delete Borrowers/Debtors' },
    },
    [PERMISSION_MODULES.PAYMENTS]: {
        view: { key: 'payments_view', label: 'View Payments' },
        create: { key: 'payments_create', label: 'Record Payments' },
        edit: { key: 'payments_edit', label: 'Edit Payments (Use with Caution)' },
        delete: { key: 'payments_delete', label: 'Delete Payments (Use with Caution)' },
    },
    [PERMISSION_MODULES.FEES]: {
        view: { key: 'fees_view', label: 'View Fees' },
        create: { key: 'fees_create', label: 'Assess Fees' },
        edit: { key: 'fees_edit', label: 'Edit/Waive Fees' },
        delete: { key: 'fees_delete', label: 'Delete Fees (Use with Caution)' },
    },
    [PERMISSION_MODULES.INVESTORS]: {
        view: { key: 'investors_view', label: 'View Investors' },
        create: { key: 'investors_create', label: 'Create Investors' },
        edit: { key: 'investors_edit', label: 'Edit Investors' },
        delete: { key: 'investors_delete', label: 'Delete Investors' },
    },
    [PERMISSION_MODULES.REMITTANCES]: {
        view: { key: 'remittances_view', label: 'View Remittances' },
        create: { key: 'remittances_create', label: 'Record Remittances' },
    },
    [PERMISSION_MODULES.REPORTS]: {
        view: { key: 'reports_view', label: 'View Reports' },
    },
    [PERMISSION_MODULES.ACCOUNTING]: {
        viewCOA: { key: 'acc_coa_view', label: 'View COA Mappings' },
        editCOA: { key: 'acc_coa_edit', label: 'Edit COA Mappings' },
        viewJE: { key: 'acc_je_view', label: 'View Journal Entries' },
        generateJE: { key: 'acc_je_generate', label: 'Generate Journal Entries' },
        postJE: { key: 'acc_je_post', label: 'Post Journal Entries' },
        viewRecon: { key: 'acc_recon_view', label: 'View Reconciliation Helper' },
        viewAudit: { key: 'acc_audit_view', label: 'View Audit Log' },
    },
    [PERMISSION_MODULES.USERS]: {
        view: { key: 'users_view', label: 'View Users' },
        create: { key: 'users_create', label: 'Create Users' },
        edit: { key: 'users_edit', label: 'Edit Users (Role, Status, Name)' },
        delete: { key: 'users_delete', label: 'Delete Users' },
    },
    [PERMISSION_MODULES.ROLES]: {
        view: { key: 'roles_view', label: 'View Roles' },
        create: { key: 'roles_create', label: 'Create Roles' },
        edit: { key: 'roles_edit', label: 'Edit Roles & Permissions' },
        delete: { key: 'roles_delete', label: 'Delete Roles' },
    },
    [PERMISSION_MODULES.LOOKUPS]: {
        view: { key: 'lookups_view', label: 'View Lookups' },
        create: { key: 'lookups_create', label: 'Create Lookups' },
        edit: { key: 'lookups_edit', label: 'Edit Lookups' },
        delete: { key: 'lookups_delete', label: 'Delete Lookups' },
    },
    [PERMISSION_MODULES.SETTINGS]: {
        view: { key: 'settings_view', label: 'View System Settings' },
        edit: { key: 'settings_edit', label: 'Edit System Settings' },
    },
};

// Function to get a default permission set (all false)
export const getDefaultPermissionSet = () => {
    const defaultPermissions = {};
    Object.keys(ALL_PERMISSIONS).forEach(moduleKey => {
        defaultPermissions[moduleKey] = {};
        Object.keys(ALL_PERMISSIONS[moduleKey]).forEach(actionKey => {
            defaultPermissions[moduleKey][actionKey] = false; // Default to false
        });
    });
    return defaultPermissions;
};

// Function to get permissions for the hardcoded Admin role (all true)
export const getAdminPermissionSet = () => {
     const adminPermissions = {};
    Object.keys(ALL_PERMISSIONS).forEach(moduleKey => {
        adminPermissions[moduleKey] = {};
        Object.keys(ALL_PERMISSIONS[moduleKey]).forEach(actionKey => {
            adminPermissions[moduleKey][actionKey] = true; // Admin gets all
        });
    });
    return adminPermissions;
};